/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    createContext,
    FunctionComponent,
    PropsWithChildren,
    ReactElement,
    useCallback,
    use,
    useEffect,
    useMemo,
    useState,
} from "react";

import { useMsal } from "@azure/msal-react";

import DataSourceService from "@/services/data-source-service";
import { useError } from "./error";
import { ResponseError } from "@/api/openapi";
import DataSource from "devextreme/data/data_source";
import { FilterDescriptor, GroupDescriptor } from "devextreme/data";

interface IDataSourcesContext {
    setService: (service: DataSourceService | undefined) => void;
    dataSourceService?: DataSourceService;
}

/**
 * The DataSourcesContext instance.
 */
const DataSourcesContext = createContext<IDataSourcesContext>({
    setService: () => {
        console.warn("No context setService handler has been set.");
    },
    dataSourceService: undefined,
});

/**
 * A function hook to get the current data sources context values.
 * @returns The data sources context values.
 *
 * @deprecated Do not use for new work. This exposes the deprecated {@link DataSourceService}; prefer the newer data-fetching approach.
 */
export const useDataSources = () => use(DataSourcesContext);

/**
 * Data sources context provider component.
 *
 * @param options The component options.
 * @returns A DataSourcesProvider component.
 */
export const DataSourcesProvider: FunctionComponent<PropsWithChildren> = (
    options,
): ReactElement => {
    const [dataSourceService, setService] = useState<
        DataSourceService | undefined
    >(undefined);
    const value = { dataSourceService, setService };
    const { instance, accounts } = useMsal();
    const errorContext = useError();

    useEffect(() => {
        if (!dataSourceService) {
            const newServiceInstance = new DataSourceService(
                errorContext,
                instance,
            );

            setService(newServiceInstance);
        }
    }, [instance, accounts, dataSourceService, errorContext]);

    return (
        <>
            <DataSourcesContext value={value}>
                {options.children}
            </DataSourcesContext>
        </>
    );
};

type DataSourceMethod = Extract<
    DataSourceService[keyof DataSourceService],
    (...args: any) => any
>;

type ExtractItems<T> = T extends DataSource<infer U> ? U : never;

type OptionalParameters<T extends (...args: any[]) => any> =
    Partial<Parameters<T>> extends infer U
        ? U extends any[]
            ? U
            : never
        : never;

/**
 * useData extracts out the logic for fetching from the datasource and returning items
 *
 * Important note! All source args are optional, so if passing `undefined` as one of your arg will
 * explode the fetch function then you MUST include that variable in the `queryOptions.skip`.
 * If you don't, the hook will return an error when that variable is undefined (because the fetch function exploded).
 *
 * @deprecated Do not use for new work. This hook (and the underlying {@link DataSourceService}) is slated for migration; prefer the newer data-fetching approach.
 */
export const useData = <
    T extends DataSourceMethod | undefined,
    Return extends T extends DataSourceMethod ? Awaited<ReturnType<T>> : never,
    U extends "datasource" | "items",
    G extends GroupDescriptor<any> | GroupDescriptor<any>[] | undefined =
        undefined,
>({
    dataSourceService,
    sourceFunction,
    sourceArgs,
    dataType,
    filter,
    group,
    queryOptions,
    enabled = true,
}: {
    dataSourceService: DataSourceService | undefined;
    sourceFunction: T;
    sourceArgs: T extends DataSourceMethod ? OptionalParameters<T> : undefined;
    dataType: U;
    filter?: FilterDescriptor | FilterDescriptor[];
    group?: G;
    queryOptions?: {
        skip?: boolean;
        disablePagination?: boolean;
    };
    enabled?: boolean;
}): {
    data: T extends DataSourceMethod
        ? U extends "items"
            ? G extends GroupDescriptor<any> | GroupDescriptor<any>[]
                ? {
                      key: string;
                      items: ExtractItems<Return>[];
                  }[]
                : ExtractItems<Return>[]
            : Return
        : undefined;
    error?: Error | ResponseError;
    isError: boolean;
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
} => {
    const [dataSource, setDataSource] = useState<Return>();
    const [error, setError] = useState<ResponseError | Error>();
    const [isLoading, setIsLoading] = useState(true);
    const [isFetching, setIsFetching] = useState(false);

    const memoizedSourceArgs = useMemo(
        () => sourceArgs,
        // eslint-disable-next-line react-hooks/use-memo
        [JSON.stringify(sourceArgs)],
    );
    const memoizedQueryOptions = useMemo(
        () => queryOptions,
        // eslint-disable-next-line react-hooks/use-memo
        [JSON.stringify(queryOptions)],
    );
    // eslint-disable-next-line react-hooks/use-memo
    const memoizedFilter = useMemo(() => filter, [JSON.stringify(filter)]);
    // eslint-disable-next-line react-hooks/use-memo
    const memoizedGroup = useMemo(() => group, [JSON.stringify(group)]);

    const loadData = useCallback(async () => {
        setIsFetching(true);
        try {
            if (memoizedQueryOptions?.skip || !sourceFunction) return;

            const boundFunction = sourceFunction.bind(dataSourceService);

            const dataSource =
                // @ts-ignore
                await boundFunction(...memoizedSourceArgs);
            if (!dataSource) return;
            // @ts-ignore
            if (memoizedFilter) dataSource.filter(memoizedFilter);
            // @ts-ignore
            if (memoizedGroup) dataSource.group(memoizedGroup);

            if (memoizedQueryOptions?.disablePagination) {
                // @ts-ignore
                dataSource.paginate(false);
            }
            // @ts-ignore
            await dataSource.reload();
            // @ts-ignore
            setDataSource(dataSource);
            setError(undefined);
        } catch (error) {
            console.error(error);
            setError(error as ResponseError | Error);
        } finally {
            setIsLoading(false);
            setIsFetching(false);
        }
    }, [
        memoizedQueryOptions?.skip,
        sourceFunction,
        dataSourceService,
        memoizedSourceArgs,
        memoizedFilter,
        memoizedGroup,
        memoizedQueryOptions?.disablePagination,
    ]);

    useEffect(() => {
        if (enabled) loadData();
    }, [loadData, enabled]);

    return {
        // @ts-ignore
        data: dataType === "items" ? dataSource?.items() : dataSource,
        error,
        isError: !!error,
        isLoading,
        isFetching,
        refetch: loadData,
    };
};
